import { UseMutateFunction } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Account, Allocation, AllocationCreateRequest, GUID, ManagementType } from '@ndvr/portfolio-api';

import { resolveClonedAllocationType } from 'views/portfolio-engine/allocation-builder/allocations/cloneAllocationType';

import { defaultCashEqSweep } from './cashEqSweepDefault';
import { useAllocationDialogSave } from './useAllocationDialogSave';

interface UsePinnedAllocationStateProps {
    initialAccounts: Account[];
    cardIsEditing: boolean;
    allocations: Allocation[];
    onAccountSelect?: (accountGUID: string | undefined) => void;
    createAllocation: UseMutateFunction<string | undefined, Error, AllocationCreateRequest, unknown>;
    canCreateSharedAllocation?: boolean;
}

export const usePinnedAllocationState = ({
    initialAccounts,
    cardIsEditing,
    allocations,
    onAccountSelect,
    createAllocation,
    canCreateSharedAllocation = false,
}: UsePinnedAllocationStateProps) => {
    const [accounts, setAccounts] = useState<Account[]>(initialAccounts);
    const [openAccountGUID, setOpenAccountGUID] = useState<string | undefined>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingAllocation, setEditingAllocation] = useState<Allocation | undefined>();
    const [dialogAccountGUID, setDialogAccountGUID] = useState<string | undefined>();

    // Sync accounts with initialAccounts when not in edit mode
    // Use JSON.stringify to compare actual data changes, not reference changes
    useEffect(() => {
        if (!cardIsEditing) {
            setAccounts(initialAccounts);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardIsEditing, JSON.stringify(initialAccounts)]);

    const handleAccountAllocationChange = (accountId: GUID<Account>, allocationGUID?: string) => {
        setAccounts((prev) =>
            prev.map((acc) =>
                acc.accountGUID === accountId
                    ? { ...acc, allocationGUID: allocationGUID ? allocationGUID : undefined }
                    : acc,
            ),
        );
    };

    const handleAccountManagementTypeChange = (accountId: GUID<Account>, managementType: ManagementType) => {
        const savedAccount = initialAccounts.find((acc) => acc.accountGUID === accountId);
        setAccounts((prev) =>
            prev.map((acc) =>
                acc.accountGUID === accountId
                    ? {
                          ...acc,
                          managementType,
                          allocationGUID:
                              managementType === ManagementType.MANUAL || managementType === ManagementType.EXTERNAL
                                  ? undefined
                                  : acc.allocationGUID,
                          cashEqSweep: defaultCashEqSweep(savedAccount ?? acc, managementType),
                      }
                    : acc,
            ),
        );
    };

    const handleAccountCashEqSweepChange = (accountId: GUID<Account>, cashEqSweep: boolean) => {
        setAccounts((prev) => prev.map((acc) => (acc.accountGUID === accountId ? { ...acc, cashEqSweep } : acc)));
    };

    const handleAccordionChange = (accountGUID: string | undefined) => {
        const normalizedGUID = accountGUID || undefined;
        setOpenAccountGUID(normalizedGUID);
        onAccountSelect?.(normalizedGUID);
    };

    const handleCreateAllocation = (accountGUID: string) => {
        setEditingAllocation(undefined);
        setDialogAccountGUID(accountGUID);
        setIsDialogOpen(true);
    };

    const handleCloneAllocation = (allocationGUID: string, accountGUID: string) => {
        const allocationToClone = allocations.find((a) => a.allocationGUID === allocationGUID);
        if (allocationToClone) {
            setEditingAllocation({
                ...allocationToClone,
                allocationGUID: undefined,
                allocationName: `${allocationToClone.allocationName} (Copy)`,
                allocationType: resolveClonedAllocationType(
                    allocationToClone.allocationType,
                    canCreateSharedAllocation,
                ),
            } as unknown as Allocation);
            setDialogAccountGUID(accountGUID);
            setIsDialogOpen(true);
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingAllocation(undefined);
        setDialogAccountGUID(undefined);
    };

    const handleAllocationDialogSave = useAllocationDialogSave({
        createAllocation,
        onSuccess: (allocationGUID) => {
            if (dialogAccountGUID) {
                handleAccountAllocationChange(dialogAccountGUID, allocationGUID);
            }
        },
        onDialogClose: handleDialogClose,
    });

    return {
        accounts,
        openAccountGUID,
        isDialogOpen,
        editingAllocation,
        dialogAccountGUID,
        handleAccountAllocationChange,
        handleAccountManagementTypeChange,
        handleAccountCashEqSweepChange,
        handleAccordionChange,
        handleCreateAllocation,
        handleCloneAllocation,
        handleDialogClose,
        handleAllocationDialogSave,
    };
};
